import { Component, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable } from 'rxjs';
import { first, map, startWith } from 'rxjs/operators';
import { Brand, Company, FileParameter, ImportRequestedProductsResponse, ListBrandsRequest, ListBrandsResponse, ProductType, ProductWorkflow, ProductWorkflowState, QualityLevel, RequestedProduct, SituViewWebApiClient, WorkflowStateType } from 'situview-webapi-client';
import { PreWorkflowTriggerStep, ProductSelectorDialogComponent, ProductSelectorDialogData, ProductSelectorResultData, WorkflowTriggerDialogComponent, WorkflowTriggerResultData } from 'src/app/components';
import { AuthService, BannerService, BannerServiceStatus, BreadcrumbService, CompanyViewService, PaginatedRequestedProducts, ProductService, StateService } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { CreateBrandDialogComponent } from '../create-brand-dialog/create-brand-dialog/create-brand-dialog.component';

@Component({
  selector: 'requested-products-list',
  templateUrl: './requested-products-list.component.html',
  styleUrls: ['./requested-products-list.component.less'],
})
export class RequestedProductsListComponent extends SituBaseComponent {

  private readonly defaultColumns = ['select', 'supplier', 'name', 'productCode', 'productType', 'description', 'brand', 'qualityLevel', 'state', 'notes', 'edit'];
  public readonly QualityLevel = QualityLevel;
  public readonly Number = Number;

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  pageNumber = 1;
  pageSize = 100;
  totalItems = 0;

  requestedProducts: RequestedProductLineItem[] = [];

  displayedColumns: string[] = this.defaultColumns;

  reportId = '';
  isReportReady = false;
  reportErrorCount = 0;
  reportPollCount = 0;

  supplierIdFilter: string | undefined;
  supplierNameFilter: string | undefined;
  productTypeFilter: string | undefined;
  productTypeNameFilter: string | undefined;
  productStateFilter: ProductWorkflowState | undefined;
  productStateNameFilter: string | undefined;
  searchTerm: string = "";

  productWorkflowState = ProductWorkflowState;
  workflowStateType = WorkflowStateType;

  filename: string | undefined;

  workflowTriggerDialogRef: MatDialogRef<WorkflowTriggerDialogComponent> | undefined;
  productSelectorDialogRef: MatDialogRef<ProductSelectorDialogComponent> | undefined;

  @ViewChild('paginator') paginator: MatPaginator | undefined;

  constructor(
    authService: AuthService,
    stateService: StateService,
    private bannerService: BannerService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private productService: ProductService,
    private companyViewService: CompanyViewService,
    private breadcrumbService: BreadcrumbService,
    private situViewClient: SituViewWebApiClient) {
    super(authService, stateService);

    this.filteredSuppliers = this.supplierControl.valueChanges.pipe(
      map((value) => {
        return this.filterSuppliers(value);
      })
    );

    this.filteredProductTypes = this.productTypeControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterProductTypes(value);
      })
    );

    this.filteredProductStates = this.productStateControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterProductStates(value);
      })
    );

    this.filteredBulkSuppliers = this.bulkSupplierControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterSuppliers(value);
      })
    );

    this.filteredBulkBrands = this.bulkBrandControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterBrands(value);
      })
    );

    this.filteredBulkProductTypes = this.bulkProductTypeControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterProductTypes(value);
      })
    );

    Promise.all([
      this.loadSuppliers(),
      this.loadProductStates(),
      this.loadProductTypes(),
      this.loadBrands(),
      this.loadData(),
    ]);
  }

  ngOnInit() {
    super.ngOnInit();
  }

  onSearch() {
    this.paginator?.firstPage();

    this.loadData(true);
  }

  onPageChange(evt: PageEvent) {
    this.pageSize = evt.pageSize;
    this.pageNumber = evt.pageIndex + 1;

    this.loadData(true);
  }

  loadData(clearRequestedProducts = true) {
    this.getRequestedProducts()
      .then((requestedProductPage: PaginatedRequestedProducts) => {
        if (clearRequestedProducts) {
          this.requestedProducts = requestedProductPage.requestedProducts!.map(x => new RequestedProductLineItem(x));
        } else {
          this.updateExistingLineItems(requestedProductPage.requestedProducts!);
        }

        this.totalItems = requestedProductPage.totalItems!;
      });
  }

  onSaveLineItem(item: RequestedProductLineItem) {
    this.saveLineItem(item);
  }

  onSaveAllEditModeLineItems() {
    this.loading = true;

    var requestedProductsToUpdate = this.requestedProducts
      .filter(x => x.editMode)
      .map(x => x.requestedProduct);

    this.productService.UpdateRequestedProducts(requestedProductsToUpdate)
      .then((requestedProducts: RequestedProduct[] | undefined) => {
        if (requestedProducts) {
          requestedProducts.forEach(x => this.updateExistingLineItem(x));
          this.showMessage("The Requested Products have been updated");
        } else {
          this.showMessage("There was an issue updating the requested products.");
        }
      })
      .finally(() => {
        this.loading = false;
      });
  }

  saveLineItem(item: RequestedProductLineItem) {
    this.loading = true;

    this.productService.UpdateRequestedProduct(item.requestedProduct)
      .then((requestedProduct: RequestedProduct | undefined) => {
        if (requestedProduct) {
          this.updateExistingLineItem(requestedProduct);
          this.showMessage("The Requested Product has been updated");
        } else {
          this.showMessage("There was an issue updating the requested product.");
        }
      })
      .finally(() => {
        this.loading = false;
      });

  }

  updateExistingLineItems(updatedRequestedProducts: RequestedProduct[]) {
    updatedRequestedProducts.forEach(x => this.updateExistingLineItem(x));
  }

  updateExistingLineItem(updatedRequestedProduct: RequestedProduct) {
    var existingLineItem = this.requestedProducts.find(y => y.requestedProduct.id === updatedRequestedProduct.id);
    if (existingLineItem) {
      existingLineItem.requestedProduct = updatedRequestedProduct;
      existingLineItem.editMode = false;
    } else {
      this.requestedProducts.push(new RequestedProductLineItem(updatedRequestedProduct));
    }
  }

  getRequestedProducts(): Promise<PaginatedRequestedProducts> {
    this.loading = true;

    var pageNumber = this.pageNumber;
    var pageSize = this.pageSize;

    var productLoadPromise = this.productService.LoadPaginatedRequestedProducts(
      undefined, // this.selectedCompany?.id!, Show all Requested products for now
      this.supplierIdFilter,
      this.productStateFilter,
      this.searchTerm,
      true,
      true,
      pageNumber, pageSize
    );

    productLoadPromise.catch((error: any) => {
      console.log(error);
    })
      .finally(() => {
        this.loading = false;
      });

    return productLoadPromise;
  }

  getWorkflowState(item: RequestedProduct): string | undefined {
    if (!item.currentWorkflowState) {
      return undefined;
    }

    var stateInfo = this.allProductStates.find(x => x.state === item.currentWorkflowState?.state)
    return stateInfo?.name;
  }

  getEditModeWorkflowState(item: RequestedProduct): string | undefined {
    if (!item.currentWorkflowState) {
      return undefined;
    }

    var stateInfo = this.allProductStates.find(x => x.state === item.currentWorkflowState?.state)
    return stateInfo?.name;
  }

  onBulkOpenWorkflowDialog() {

    var processResult = async (result: WorkflowTriggerResultData) => {

      var count = 0;
      var errorCount = 0;

      for (var lineItem of this.requestedProducts) {

        if (lineItem.editMode) {
          var requestedProduct = lineItem.requestedProduct;
          var triggerResult = await this.productService.TriggerRequestedProductWorkflow(requestedProduct.id!, result.selectedTransition?.trigger, result.comments);
          if (lineItem.originalRequestedProduct) {
            lineItem.originalRequestedProduct.currentWorkflowState = triggerResult;
          }

          count++;
          if (triggerResult) {
            requestedProduct.currentWorkflowState = triggerResult;
          } else {
            errorCount++;
          }

          this.showMessage(`Workflow transitioned ${count} requested products with ${errorCount} errors!`);
        }
      }
    }

    // It should not be possible to get here unless all the workflow states are the same
    var workflowState = this.workflowStateOfRequestedProductsInEditModeAreTheSame();
    this.openWorkflowDialog(workflowState!, undefined, processResult);

  }

  onOpenWorkflowDialog(item: RequestedProductLineItem) {

    var requestedProduct = item.requestedProduct;
    var processResult = async (result: WorkflowTriggerResultData) => {
      var triggerResult = await this.productService.TriggerRequestedProductWorkflow(requestedProduct.id!, result.selectedTransition?.trigger, result.comments);

      if (triggerResult) {
        var stateHasChanged = requestedProduct.currentWorkflowState?.state !== triggerResult?.state;
        requestedProduct.currentWorkflowState = triggerResult;
        if (item.originalRequestedProduct) {
          item.originalRequestedProduct.currentWorkflowState = triggerResult;
        }

        if (stateHasChanged) {
          this.showMessage(`Workflow successfully transitioned to ${triggerResult.name}!`);
        } else {
          this.showMessage(`Comment successfully added!`);
        }
      } else {
        this.showMessage("Failed to update workflow!");
      }

    }

    var preWorkflowTriggerSteps: PreWorkflowTriggerStep[] = [];

    // The the Requested product is marked as a duplicate, allow the user to select the product
    var setDuplicateProductId = {
      requiredProperty: "DuplicateProductId",
      preTriggerFunction: async () => { return await this.openProductSelector(item); }
    } as PreWorkflowTriggerStep;
    preWorkflowTriggerSteps.push(setDuplicateProductId);

    this.openWorkflowDialog(requestedProduct.currentWorkflowState!, requestedProduct, processResult, preWorkflowTriggerSteps);
  }

  openWorkflowDialog(workflowState: ProductWorkflow, workflowSubject: any | undefined,
    processResult: (result: WorkflowTriggerResultData) => void,
    preWorkflowTriggerSteps: PreWorkflowTriggerStep[] | undefined = undefined) {
    const dialogWidth = '800px';
    const dialogHeight = '500px';

    this.workflowTriggerDialogRef = this.dialog.open(WorkflowTriggerDialogComponent, {
      width: dialogWidth,
      height: dialogHeight,
      data: {
        workflowSubject: workflowSubject,
        workflowState: workflowState,
        preWorkflowTriggerSteps: preWorkflowTriggerSteps,
      }
    });

    this._subscriptions.push(this.workflowTriggerDialogRef.afterClosed()
      .subscribe((data: WorkflowTriggerResultData) => {
        if (!data) {
          // Dialog was closed without triggering workflow
          return;
        }

        processResult(data);
      })
    );
  }

  async openProductSelector(item: RequestedProductLineItem): Promise<boolean> {

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    const dialogWidth = `${Math.round((70 / 100) * screenWidth)}px`;
    const dialogHeight = `${Math.round((90 / 100) * screenHeight)}px`;

    var dialogData = new ProductSelectorDialogData();
    dialogData.companyId = this.selectedCompany?.id;
    dialogData.multiSelect = false;
    dialogData.tagSelectionEnabled = false;

    this.productSelectorDialogRef = this.dialog.open(ProductSelectorDialogComponent, {
      width: dialogWidth,
      height: dialogHeight,
      data: dialogData,
    });

    var result: ProductSelectorResultData = await this.productSelectorDialogRef.afterClosed().toPromise();

    if (!result?.products[0]) {
      return false;
    }

    item.requestedProduct.duplicateProductId = result.products[0].id;

    await this.saveLineItem(item);

    return true;
  }

  workflowStateOfRequestedProductsInEditModeAreTheSame(): ProductWorkflow | undefined {
    // Filter the objects to find the selected ones
    const editModeProducts = this.requestedProducts.filter(x => x.editMode);

    // Check if there are no selected objects
    if (editModeProducts.length === 0) {
      return undefined;
    }

    var firstWorkflowState = editModeProducts[0].requestedProduct.currentWorkflowState;
    // Check if all workflow states are the same
    const allSame = editModeProducts.every(x => x.requestedProduct.currentWorkflowState!.state === firstWorkflowState!.state);

    return allSame ? firstWorkflowState : undefined;
  }

  isCompanyName(name: string): boolean {
    return this.allCompanies.some(x => x.name === name)
  }

  getProductTypeName(item: RequestedProduct): string | undefined {
    var productType = this.allProductTypes.find(x => x.id === item.productTypeId)
    return productType?.name;
  }

  isProductTypeName(name: string): boolean {
    return this.allProductTypes.some(x => x.name === name)
  }

  onImportRequestedProductsClick() {
    document.getElementById("upload-requested-product-file")?.click();
  }

  onFileChosen(event: any) {
    var fileName = event.target.files[0].name;

    if (!fileName) {
      return; // no file was selected
    }

    var fileExt = fileName.split('.').pop();
    if (fileExt !== 'xlsx') {
      this.showMessage("Only Excel spreadsheets are allowed.");
      return;
    }

    this.loading = true;
    let fileParameter: FileParameter = { data: event.target.files[0], fileName: event.target.files[0].name };

    this.situViewClient.importRequestedProduct(this.selectedCompany?.id, fileParameter)
      .then((response: ImportRequestedProductsResponse) => {
        if (response.success) {
          this.showMessage("Requested Products file uploaded");
          this.requestedProducts = response.requestedProducts!.map(x => new RequestedProductLineItem(x));
        } else {
          this.showMessage("There was an issue importing the requested product file.");

          response.errorMessages?.forEach(x => {
            this.bannerService.AddMessage(x, BannerServiceStatus.error, null, true);
          });
        }
      })
      .finally(() => {
        this.loading = false;
      });
  }

  onLineItemSupplierSelected(event: any, lineItem: RequestedProductLineItem): void {
    var supplierName = event.option.value;
    lineItem.requestedProduct.supplierId = this.allCompanies?.find(x => x.name === supplierName)?.id;
    lineItem.supplierNameFilter = supplierName;
  }

  onLineItemProductTypeSelected(event: any, lineItem: RequestedProductLineItem): void {
    var productTypeName = event.option.value;
    lineItem.requestedProduct.productTypeId = this.allProductTypes?.find(x => x.name === productTypeName)?.id;
    lineItem.productTypeNameFilter = productTypeName;
  }

  onLineItemBrandSelected(event: any, lineItem: RequestedProductLineItem): void {
    var brandName = event.option.value;
    lineItem.requestedProduct.brandId = this.allBrands?.find(x => x.name === brandName)?.id;
  }

  hasEditModeLineItems(): boolean {
    if (!this.requestedProducts) {
      return false;
    }

    return this.requestedProducts.some(x => x.editMode);
  }

  countEditModeLineItems(): number {
    var count = 0;
    if (this.requestedProducts) {
      
      for (var requestedProduct of this.requestedProducts) {
        if (requestedProduct.editMode) {
          count++;
        }
      }
    }
    
    return count;
  }

  editSameRequestedSupplierName(requestedSupplierName: string, enable: boolean) {
    this.requestedProducts.forEach(x => {
      if (x.requestedProduct.requestedSupplierName === requestedSupplierName) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameRequestedProductTypeName(requestedProductTypeName: string, enable: boolean) {
    this.requestedProducts.forEach(x => {
      if (x.requestedProduct.requestedProductTypeName === requestedProductTypeName) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameRequestedBrand(requestedBrand: string, enable: boolean) {
    this.requestedProducts.forEach(x => {
      if (x.requestedProduct.requestedBrand === requestedBrand) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameRequestedQualityLevel(requestedQualityLevel: QualityLevel, enable: boolean) {
    this.requestedProducts.forEach(x => {
      if ((x.requestedProduct.requestedQualityLevel ?? null) === (requestedQualityLevel ?? null)) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameProductWorkflowState(productState: ProductWorkflowState, enable: boolean) {
    this.requestedProducts.forEach(x => {
      if (x.requestedProduct.currentWorkflowState!.state === productState) {
        this.editLineItem(x, enable)
      }
    });
  }

  editAllLineItems(enable: boolean) {
    this.requestedProducts.forEach(x => {
      if (x.editMode !== enable) {
        this.editLineItem(x, enable);
      }
    });
  }

  editLineItem(item: RequestedProductLineItem, enable: boolean) {
    if (enable) {
      this.enableEditMode(item);
    } else {
      this.cancelEditMode(item);
    }
  }

  enableEditMode(item: RequestedProductLineItem) {
    item.editMode = true;
    item.originalRequestedProduct = new RequestedProduct(item.requestedProduct);

    item.filteredSuppliers = item.supplierControl.valueChanges.pipe(
      startWith(item.supplierControl.value),
      map((value) => this.filterSuppliers(value))
    );

    item.filteredProductTypes = item.productTypeControl.valueChanges.pipe(
      startWith(item.productTypeControl.value),
      map((value) => this.filterProductTypes(value))
    );

    item.filteredBrands = item.brandControl.valueChanges.pipe(
      startWith(item.brandControl.value),
      map((value) => this.filterBrands(value, item.requestedProduct.supplierId))
    );

    item.brandControl.setValue(this.brandDirectory[item.requestedProduct.brandId || '']);
    item.brandControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });

    item.supplierNameFilter = this.companyDirectory[item.requestedProduct.supplierId || ''];
    item.supplierControl.setValue(item.supplierNameFilter);
    item.supplierControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });

    item.productTypeNameFilter = this.allProductTypes?.find(x => x.id === item.requestedProduct.productTypeId)?.name;
    item.productTypeControl.setValue(item.productTypeNameFilter);
    item.productTypeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  cancelEditMode(item: RequestedProductLineItem) {
    item.editMode = false;
    if (item.originalRequestedProduct) {
      item.requestedProduct = item.originalRequestedProduct;
      item.originalRequestedProduct = undefined;
    }
  }

  /*  Supplier AutoComplete Filter */
  supplierControl = new FormControl();
  filteredSuppliers: Observable<Company[]> | undefined;
  allCompanies: Company[] = [];
  companyDirectory: Record<string, string> = {};

  private loadSuppliers() {
    return this.companyViewService.LoadCompanies()
      .then((companies: Company[]) => {
        this.allCompanies = companies;
        companies.forEach(c => this.companyDirectory[c.id!] = c.name!);

        this.supplierNameFilter = this.allCompanies?.find(x => x.id === this.supplierIdFilter)?.name;
        this.supplierControl.setValue(this.supplierNameFilter);
        this.supplierControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      });
  }

  onSupplierSelected(event: any): void {
    this.selectSupplierByName(event.option.value);
  }

  selectSupplierByName(supplierName: string) {
    this.supplierIdFilter = this.allCompanies?.find(x => x.name === supplierName)?.id;
    this.supplierNameFilter = supplierName;
  }

  clearSupplier() {
    this.supplierControl.reset();
    this.supplierIdFilter = undefined;
    this.supplierNameFilter = undefined;
  }

  private filterSuppliers(searchTerm: string | undefined = undefined): Company[] {
    var companies = this.allCompanies;

    // Filter Company Names by the supplied Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      companies = companies.filter(x => x.name).filter(x => x.name!.toLowerCase().includes(searchTerm!));
    }

    return companies;
  }
  /*  Supplier AutoComplete Filter */

  /*  ProductType AutoComplete Filter */
  productTypeControl = new FormControl();
  filteredProductTypes: Observable<ProductType[]> | undefined;
  allProductTypes: ProductType[] = [];

  private loadProductTypes() {
    return this.productService.LoadProductTypes()
      .then((productTypes: ProductType[]) => {
        this.allProductTypes = productTypes;

        this.productTypeNameFilter = this.allProductTypes?.find(x => x.id === this.productTypeFilter)?.name;
        this.productTypeControl.setValue(this.productTypeNameFilter);
        this.productTypeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      });
  }

  onProductTypeSelected(event: any): void {
    this.selectProductTypeByName(event.option.value);
  }

  selectProductTypeByName(productTypeName: string) {
    this.productTypeFilter = this.allProductTypes?.find(x => x.name === productTypeName)?.id;
    this.productTypeNameFilter = productTypeName;
  }

  selectProductTypeById(productTypeId: string) {
    this.productTypeFilter = productTypeId;
    this.productTypeNameFilter = this.allProductTypes?.find(x => x.id === productTypeId)?.name;
  }

  selectProductTypeByProduct(productTypeId: string) {
    this.productTypeFilter = productTypeId;
    this.productTypeNameFilter = this.allProductTypes?.find(x => x.id === productTypeId)?.name;

    this.productTypeControl.setValue(this.productTypeNameFilter);
    this.productTypeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  onClearProductType() {
    this.clearProductType();
  }

  clearProductType() {
    this.productTypeControl.reset();
    this.productTypeFilter = undefined;
    this.productTypeNameFilter = undefined;
  }

  private filterProductTypes(searchTerm: string | undefined = undefined): ProductType[] {
    var productTypes = this.allProductTypes;

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      productTypes = productTypes.filter(x => x.name).filter(x => x.name!.toLowerCase().includes(searchTerm!));
    }

    return productTypes;
  }
  /*  ProductType AutoComplete Filter */
  /* #region Brand AutoComplete Filter */

  private filterBrands(searchTerm: string | undefined = undefined, supplierId?: string): ProductType[] {
    var brands = this.allBrands;

    if (supplierId) {
      brands = brands.filter(brand => brand.companyId === supplierId);
    }

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      brands = brands.filter(x => x.name).filter(x => x.name!.toLowerCase().includes(searchTerm!));
    }

    return brands;
  }

  /* #endregion Brand AutoComplete Filter */




  /*  ProductState AutoComplete Filter */
  productStateControl = new FormControl();
  filteredProductStates: Observable<ProductWorkflow[]> | undefined;
  allProductStates: ProductWorkflow[] = [];

  private loadProductStates() {
    return this.productService.LoadProductWorkflowConfiguration()
      .then((productWorkflow: ProductWorkflow[]) => {
        this.allProductStates = productWorkflow;

        this.productStateNameFilter = this.allProductStates?.find(x => x.state === this.productStateFilter)?.name;
        this.productStateControl.setValue(this.productStateNameFilter);
        this.productStateControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      });
  }

  onProductStateSelected(event: any): void {
    this.selectProductStateByName(event.option.value);
  }

  selectProductState(productState: ProductWorkflowState) {
    this.productStateFilter = productState;
    this.productStateNameFilter = this.allProductStates?.find(x => x.state === productState)?.name;
  }

  selectProductStateByName(productStateName: string) {
    this.productStateFilter = this.allProductStates?.find(x => x.name === productStateName)?.state;
    this.productStateNameFilter = productStateName;
  }

  onClearProductState() {
    this.clearProductState();
  }

  clearProductState() {
    this.productStateControl.reset();
    this.productStateFilter = undefined;
    this.productStateNameFilter = undefined;
  }

  private filterProductStates(searchTerm: string | undefined = undefined): ProductWorkflow[] {
    var productStates = this.allProductStates;

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      productStates = productStates.filter(x => x.name).filter(x => x.name!.toLowerCase().includes(searchTerm!));
    }

    return productStates;
  }

  /*  ProductState AutoComplete Filter */

  /*  Bulk Supplier AutoComplete Filter */
  bulkSupplierControl = new FormControl();
  filteredBulkSuppliers: Observable<Company[]> | undefined;

  onBulkSupplierSelected(event: any) {
    var supplierName = event.option.value;
    var supplierId = this.allCompanies?.find(x => x.name === supplierName)?.id;

    return this.requestedProducts.forEach(x => {
      if (x.editMode) {
        x.supplierNameFilter = supplierName;
        x.requestedProduct.supplierId = supplierId;
        x.supplierControl.setValue(x.supplierNameFilter);
        x.supplierControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      }
    });
  }

  clearBulkSupplier() {
    this.bulkSupplierControl.reset();
  }

  /*  Bulk Supplier AutoComplete Filter */

  /*  Bulk Product Type AutoComplete Filter */
  bulkProductTypeControl = new FormControl();
  filteredBulkProductTypes: Observable<ProductType[]> | undefined;

  onBulkProductTypeSelected(event: any) {
    var productTypeName = event.option.value;
    var productTypeId = this.allProductTypes?.find(x => x.name === productTypeName)?.id;

    return this.requestedProducts.forEach(x => {
      if (x.editMode) {
        x.productTypeNameFilter = productTypeName;
        x.requestedProduct.productTypeId = productTypeId;
        x.productTypeControl.setValue(x.productTypeNameFilter);
        x.productTypeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      }
    });
  }

  clearBulkProductType() {
    this.bulkProductTypeControl.reset();
  }

  /*  Bulk Product Type AutoComplete Filter */

  /*  Bulk Brand AutoComplete Filter */
  bulkBrandControl = new FormControl();
  allBrands: Required<Brand>[] = [];
  brandDirectory: Record<string, string> = {};

  filteredBulkBrands: Observable<Brand[]> | undefined;

  private loadBrands() {
    return this.situViewClient.listBrands(new ListBrandsRequest())
      .then((res: ListBrandsResponse) => {
        let brands = res.brands;
        this.allBrands = brands as Required<Brand>[] || [];
        this.allBrands.sort((a, b) => a.name.localeCompare(b.name));
        this.brandDirectory = {};
        this.allBrands.forEach(b => this.brandDirectory[b.id!] = b.name!);
      });
  }

  onBulkBrandSelected(event: any) {
    var brandName = event.option.value;
    var brandId = this.allBrands.find(x => x.name === brandName)?.id;

    return this.requestedProducts.forEach(x => {
      if (x.editMode) {
        x.requestedProduct.brandId = brandId;
        x.brandControl.setValue(brandName);
        x.supplierControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      }
    });
  }

  clearBulkBrand() {
    this.bulkBrandControl.reset();
  }

  onBulkBrandChanged(event: any) {
    return this.requestedProducts.forEach(x => {
      if (x.editMode) {
        x.requestedProduct.brand = event.target.value;
      }
    });
  }
  /*  Bulk Brand AutoComplete Filter */

  onBulkQualityLevelChanged(event: any) {
    return this.requestedProducts.forEach(x => {
      if (x.editMode) {
        let value = event.target.value
        x.requestedProduct.qualityLevel = value ? Number(event.target.value) : undefined;
      }
    });
  }

  onBulkNotesChanged(event: any) {
    return this.requestedProducts.forEach(x => {
      if (x.editMode) {
        x.requestedProduct.notes = event.target.value;
      }
    });
  }


  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }

}

class RequestedProductLineItem {

  requestedProduct: RequestedProduct;
  originalRequestedProduct: RequestedProduct | undefined;

  editMode = false;

  supplierControl = new FormControl();
  filteredSuppliers: Observable<Company[]> | undefined;
  supplierNameFilter: string | undefined;

  productTypeControl = new FormControl();
  filteredProductTypes: Observable<ProductType[]> | undefined;
  productTypeNameFilter: string | undefined;

  brandControl: FormControl;
  filteredBrands: Observable<Brand[]> | undefined;

  constructor(requestedProduct: RequestedProduct) {
    this.requestedProduct = requestedProduct;
    this.brandControl = new FormControl();
  }

  allowEditMode() {
    var currentState = this.requestedProduct.currentWorkflowState!.state;
    return currentState == ProductWorkflowState.NewRequest || currentState == ProductWorkflowState.OnHold;
  }

  clearSupplier() {
    this.supplierControl.reset();
    this.requestedProduct.supplierId = undefined;
    this.supplierNameFilter = undefined;
  }

  clearProductType() {
    this.productTypeControl.reset();
    this.requestedProduct.productTypeId = undefined;
    this.productTypeNameFilter = undefined;
  }

  clearBrand() {
    this.brandControl.reset();
    this.requestedProduct.brandId = undefined;
  }
}